import { useEffect, useState } from 'react';

import styled from 'styled-components';

import { Collapse } from '@components/animators/collapse';
import { Badge } from '@components/badge';
import { Button } from '@components/button';
import { EmptyState } from '@components/empty-state';
import { FocusBox } from '@components/focus-box';
import { HideContainer } from '@components/hide-container';
import { IconBranch } from '@components/icons/branch';
import { IconDropdown } from '@components/icons/dropdown';
import { IconFormatText } from '@components/icons/format-text';
import { IconList } from '@components/icons/list';
import { IconMappingOff } from '@components/icons/mapping-off';
import { IconMappingOn } from '@components/icons/mapping-on';
import { IconReturn } from '@components/icons/return';
import { IconSearch } from '@components/icons/search';
import { IconTextShort } from '@components/icons/text-short';
import { Menu } from '@components/menu';
import { SearchBox } from '@components/search-box';
import {
  CloseButton,
  ContentSeparator,
  hideExtendedSidebar,
  SimpleIconHeader,
} from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text/text';
import { useTimeoutState } from '@hooks/use-timeout-state';
import { useScopedTranslate } from '@locale/translate';
import { useSelectionStore } from '@stores/create-selection-store';
import { scrollbarStyle, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { unasteriskLabel } from '@utils/strings';
import { MakePropertiesRequired, Transient } from '@utils/types';

import {
  extractName,
  FieldMappingData,
  FieldMappingFieldItem,
  FieldMappingGroup,
  FieldType,
  getPlaceholderHtml,
  getVariableLabel,
  MappableVariable,
} from './addons/field-mapping';
import { fieldTypeIconMap, fieldTypeLabelMap } from './addons/field-mapping-type-maps';
import { actionIcons, EditorRefProp } from './common';
import { FIELD_MAPPING_TEXT_EDITOR_CLASS_NAME } from './field-mapping-text-editor';
import { DateManualField } from './fields/date-field';
import { FileManualField } from './fields/file-field';
import { NumberManualField } from './fields/number-field';
import { PhoneManualField } from './fields/phone-field';
import { SelectManualField } from './fields/select-field';

export type FieldMappingSidebarProps = MakePropertiesRequired<FieldMappingData, 'variableGroups'> &
  EditorRefProp & {
    onSelect?(): void;
    fieldType: FieldType;
    label?: string;
    fieldMappingId: string;
    onManualSelect?: (value: any) => void;
    manualData?: any;
    withCardBackground?: boolean;
    variable?: FieldMappingFieldItem;
  };

const TRAVELLER_IDENTIFIER = 'traveller';
const COMPANY_IDENTIFIER = 'company';
const ACTION_GROUPS_IDENTIFIER = 'actions-in-journey';
const SIDEBAR_CONTAINER_CLASS_NAME = 'field-mapping-sidebar-container';

export const FieldMappingSidebar = ({
  variableGroups,
  editorRef,
  onSelect,
  fieldType,
  label = '',
  fieldMappingId,
  onManualSelect,
  manualData,
  variable,
}: FieldMappingSidebarProps) => {
  const { t } = useScopedTranslate(locale);
  const [search, setSearch] = useState('');
  const [focusedId, setFocusedId] = useTimeoutState({ defaultValue: '', timeout: 1000 });
  const variableName = variable ? extractName(variable as MappableVariable) : '';

  const loweredSearch = search.toLowerCase();
  const filteredGroups = variableGroups
    .map((group) => {
      const filteredFields = group.fields.filter((field) =>
        field.label?.toLowerCase().includes(loweredSearch)
      );
      if (filteredFields.length === 0) {
        return null;
      }
      return {
        ...group,
        fields: filteredFields,
      };
    })
    .filter(Boolean) as FieldMappingGroup[];

  const travellerGroup = filteredGroups.find((group) => group.id === TRAVELLER_IDENTIFIER);
  const companyGroup = filteredGroups.find((group) => group.id === COMPANY_IDENTIFIER);
  const actionGroups = filteredGroups.filter((group) => group.fields.some((field) => field.action));

  const otherGroups = filteredGroups.filter(
    (group) =>
      group.id !== COMPANY_IDENTIFIER &&
      group.id !== TRAVELLER_IDENTIFIER &&
      actionGroups.every((actionGroup) => actionGroup.id !== group.id)
  );

  const expandedStore = useSelectionStore(`field-mapping-expanded-${fieldMappingId}`, {
    [TRAVELLER_IDENTIFIER]: true,
    [COMPANY_IDENTIFIER]: true,
    [ACTION_GROUPS_IDENTIFIER]: true,
    ...Object.fromEntries(otherGroups.map((group) => [group.id, true])),
  });

  useEffect(() => {
    if (variableName) {
      const containingGroup = variableGroups.find((group) =>
        group.fields.some((field) => {
          const fieldName = extractName(field as MappableVariable);
          return fieldName === variableName;
        })
      );

      if (containingGroup) {
        if (containingGroup.action) {
          expandedStore.add(ACTION_GROUPS_IDENTIFIER);
        }
        expandedStore.add(containingGroup.id);
      }
      // We need to allow time for the sidebar to expand, else the focus effect
      // will be rendered behind the normal sidebar
      setTimeout(() => setFocusedId(variableName), 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variableName]);

  useEffect(() => {
    const handleFocus = (event: FocusEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      const isTextInput =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        (target.tagName === 'DIV' && target.className.includes('ProseMirror'));

      if (isTextInput) {
        const isInsideFieldMappingEditor = Boolean(
          target.closest(`.${FIELD_MAPPING_TEXT_EDITOR_CLASS_NAME}`)
        );
        const isInsideSidebar = Boolean(target.closest(`.${SIDEBAR_CONTAINER_CLASS_NAME}`));
        const isInsidePopup = Boolean(target.closest('.popover-panel'));

        if (!isInsideFieldMappingEditor && !isInsideSidebar && !isInsidePopup) {
          hideExtendedSidebar();
        }
      }
    };

    window.addEventListener('focusin', handleFocus);
    return () => window.removeEventListener('focusin', handleFocus);
  }, []);

  const handleSelect = (
    { options, icon, iconRight, isActive, ...field }: FieldMappingFieldItem,
    insertType?: 'label' | 'value' | 'labelAndValue'
  ) => {
    onSelect?.();
    const variableLabel = getVariableLabel(field, variableGroups);
    const variableName = extractName(field as MappableVariable);
    const placeholder = getPlaceholderHtml({
      ...field,
      variable: variableName,
      label: variableLabel,
    });
    const content =
      insertType === 'labelAndValue'
        ? `${field.label}: ${placeholder}<br />`
        : insertType === 'label'
        ? field.label
        : placeholder;
    editorRef.current?.insertContent(content);
  };
  const hasActionGroups = actionGroups.length > 0;

  const isAllCollapsed =
    !expandedStore.isSelected(ACTION_GROUPS_IDENTIFIER) &&
    !filteredGroups.some((group) => !group.action && expandedStore.isSelected(group.id));

  const toggleAllExpanded = () => {
    if (isAllCollapsed) {
      if (hasActionGroups) {
        expandedStore.add(ACTION_GROUPS_IDENTIFIER);
      }
      filteredGroups.forEach((group) => {
        expandedStore.add(group.id);
      });
    } else {
      expandedStore.clear();
    }
  };

  const isEmptySearch = variableGroups.length > 0 && filteredGroups.length === 0 && search !== '';
  const isNoVariables = variableGroups.length === 0;

  return (
    <Container className={SIDEBAR_CONTAINER_CLASS_NAME}>
      <Stack space={4} style={{ height: '100%' }}>
        <Stack space={6}>
          <Stack space={4} style={{ flexShrink: 0 }}>
            <Stack space={2} orientation="horizontal" align="center" justify="space-between">
              <SimpleIconHeader icon={IconMappingOn} iconColor="blue100" title={t('fieldMap')} />
              <Stack space={2} orientation="horizontal" align="center">
                <CloseButton onClose={hideExtendedSidebar} />
              </Stack>
            </Stack>
            <Stack orientation="horizontal" space={4} justify="space-between" align="center">
              <SearchBox value={search} onChange={setSearch} placeholder={t('core.search')} />
              <Button
                variant="secondary"
                text={t(isAllCollapsed ? 'expandAll' : 'collapseAll')}
                onClick={toggleAllExpanded}
                iconRight={<IconDropdown rotateDegrees={isAllCollapsed ? 0 : 270} />}
                style={{ minWidth: pxToRem(144) }}
              />
            </Stack>
          </Stack>
          <ContentSeparator />
          <FieldLabelCard
            isShowingAll={variableGroups.length > 0 && !search}
            fieldType={fieldType}
            label={label}
            editorRef={editorRef}
            onManualSelect={onManualSelect}
            manualData={manualData}
            hasBackground
          />
          {isEmptySearch && (
            <EmptyState
              variant="small"
              skipMargin
              icon={<IconSearch />}
              header={t('noResultsFound')}
              content={t('noResultsMatchingSearch')}
            />
          )}
          {isNoVariables && <NoVariablesState />}
        </Stack>
        <FieldsContainer>
          <Stack space={2}>
            {travellerGroup && (
              <VariableGroup
                group={travellerGroup}
                description={t('travellerDescription')}
                onSelect={handleSelect}
                isCollapsed={!expandedStore.isSelected(TRAVELLER_IDENTIFIER)}
                onCollapseToggle={() => expandedStore.toggle(TRAVELLER_IDENTIFIER)}
                fieldType={fieldType}
                focusId={focusedId}
              />
            )}
            {companyGroup && (
              <VariableGroup
                group={companyGroup}
                onSelect={handleSelect}
                isCollapsed={!expandedStore.isSelected(COMPANY_IDENTIFIER)}
                onCollapseToggle={() => expandedStore.toggle(COMPANY_IDENTIFIER)}
                fieldType={fieldType}
                focusId={focusedId}
              />
            )}
            {hasActionGroups && (
              <HideContainer
                title={t('actionsInJourney')}
                counter={actionGroups.length}
                defaultClosed={!expandedStore.isSelected(ACTION_GROUPS_IDENTIFIER)}
                key={`${ACTION_GROUPS_IDENTIFIER}-${!expandedStore.isSelected(
                  ACTION_GROUPS_IDENTIFIER
                )}`}
                onClick={() => expandedStore.toggle(ACTION_GROUPS_IDENTIFIER)}
              >
                <Stack space={2}>
                  {actionGroups.map((group) => (
                    <VariableGroup
                      key={group.id}
                      isActionGroup
                      group={group}
                      description={group.description}
                      onSelect={handleSelect}
                      isCollapsed={!expandedStore.isSelected(group.id)}
                      onCollapseToggle={() => expandedStore.toggle(group.id)}
                      fieldType={fieldType}
                      focusId={focusedId}
                    />
                  ))}
                </Stack>
              </HideContainer>
            )}
            {otherGroups.map((group) => (
              <VariableGroup
                key={group.id}
                group={group}
                onSelect={handleSelect}
                isCollapsed={!expandedStore.isSelected(group.id)}
                onCollapseToggle={() => expandedStore.toggle(group.id)}
                fieldType={fieldType}
                focusId={focusedId}
              />
            ))}
          </Stack>
        </FieldsContainer>
      </Stack>
    </Container>
  );
};

const NoVariablesState = () => {
  const { t } = useScopedTranslate(locale);
  return (
    <Stack space={4}>
      <EmptyState
        variant="small"
        skipMargin
        icon={<IconMappingOff />}
        header={t('noMappableFields')}
        content=""
      />
      <div>
        <Text text={t('possibleReasons')} font="regular14" color="textHelper" />
        <ul>
          <li style={{ color: theme.color.textHelper }}>
            <Text text={t('noActionsAbove')} font="regular14" color="textHelper" />
          </li>
          <li style={{ color: theme.color.textHelper }}>
            <Text text={t('noFieldsSameType')} font="regular14" color="textHelper" />
          </li>
        </ul>
      </div>
    </Stack>
  );
};

type VariableGroupProps = {
  group: FieldMappingGroup;
  description?: string;
  isCollapsed?: boolean;
  onSelect(field: FieldMappingFieldItem, insertType?: 'label' | 'value' | 'labelAndValue'): void;
  isActionGroup?: boolean;
  onCollapseToggle(id: string): void;
  fieldType: FieldType;
  focusId?: string;
};

const VariableGroup = ({
  group,
  description,
  isCollapsed,
  onSelect,
  isActionGroup = false,
  onCollapseToggle,
  fieldType,
  focusId,
}: VariableGroupProps) => {
  const { t } = useScopedTranslate(locale);
  const ICON_SIZE = 16;

  const Content = (
    <Stack space={4}>
      {!isActionGroup && description && (
        <Text text={description} font="regular14" color="textSecondary" />
      )}
      <Stack
        space={2}
        style={
          isActionGroup
            ? {
                margin: `${pxToRem(16)} ${pxToRem(-24)} 0 ${pxToRem(-24)}`,
                padding: `0 ${pxToRem(24)}`,
              }
            : {}
        }
      >
        {group.fields.map((field) => {
          const key = `${field.source}${field.field}`;
          if (field.type === 'separator') {
            return <ContentSeparator key={key} />;
          }
          const focusCompareField = extractName(field as MappableVariable);

          const Icon = fieldTypeIconMap[field.type ?? 'text'] ?? IconTextShort;
          const isSelect =
            (field.type === 'select' || field.type === 'multi_select') &&
            (field.label.includes('(label)') ||
              field.label.includes('(labels)') ||
              field.label.includes('(value)') ||
              field.label.includes('(values)'));

          if (!isSelect) {
            return (
              <FocusBox isFocused={focusId === focusCompareField} key={key} padding={4}>
                <Stack orientation="horizontal" space={2} align="center" justify="space-between">
                  <Stack orientation="horizontal" space={2} align="center">
                    <Icon size={ICON_SIZE} color="elementPurple80" />
                    <Text text={field.label} font="regular14" />
                  </Stack>
                  <AddButton field={field} onSelect={onSelect} fieldType={fieldType} />
                </Stack>
              </FocusBox>
            );
          }
          const isSelectLabel = field.label.includes('(label)') || field.label.includes('(labels)');

          const isSelectValue = field.label.includes('(value)') || field.label.includes('(values)');

          const LabelObject = (
            <Stack orientation="horizontal" space={2} align="center">
              <IconList size={ICON_SIZE} color="elementPurple80" />
              <Text
                text={field.label.replace('(label)', '').replace('(labels)', '')}
                font="regular14"
              />
            </Stack>
          );

          return (
            <>
              {isSelectLabel && LabelObject}
              <FocusBox isFocused={focusId === focusCompareField} key={key} padding={4}>
                <Stack orientation="horizontal" space={2} align="center" justify="space-between">
                  <Stack orientation="horizontal" space={2}>
                    <FieldIndentBorder $isValue={isSelectValue} />
                    <Stack orientation="horizontal" space={2} align="center">
                      <IconTextShort size={ICON_SIZE} color="elementPurple80" />
                      <Text
                        text={isSelectValue ? t('optionValue') : t('optionLabel')}
                        font="regular14"
                      />
                    </Stack>
                  </Stack>
                  <AddButton field={field} onSelect={onSelect} fieldType={fieldType} />
                </Stack>
              </FocusBox>
            </>
          );
        })}
      </Stack>
    </Stack>
  );

  if (isActionGroup) {
    const { Icon, color } = actionIcons[group.action ?? 'send_email'];
    return (
      <FieldLabelCardContainer>
        <GroupTitle
          group={group}
          isCollapsed={isCollapsed}
          isActionGroup={isActionGroup}
          onClick={() => onCollapseToggle(group.id)}
          description={isActionGroup ? description : ''}
          icon={isActionGroup ? <Icon color={color} /> : undefined}
        />
        <Collapse overflowType="visible" isCollapsed={Boolean(isCollapsed)}>
          {Content}
        </Collapse>
      </FieldLabelCardContainer>
    );
  }
  return (
    <HideContainer
      title={group.label}
      defaultClosed={isCollapsed}
      key={`${group.id}-${isCollapsed}`}
      onClick={() => onCollapseToggle(group.id)}
      overflowType="visible"
    >
      {Content}
    </HideContainer>
  );
};

const ComboContainer = styled.div({
  display: 'flex',
  '&[data-has-menu="true"]': {
    '>button:first-child': {
      borderTopRightRadius: pxToRem(0),
      borderBottomRightRadius: pxToRem(0),
    },
    '.menu-button': {
      marginLeft: pxToRem(2),
      borderTopLeftRadius: pxToRem(0),
      borderBottomLeftRadius: pxToRem(0),
      width: pxToRem(32),
    },
  },
});

type AddButtonProps = Pick<VariableGroupProps, 'onSelect' | 'fieldType'> & {
  field: FieldMappingFieldItem;
};

const AddButton = ({ field, onSelect, fieldType }: AddButtonProps) => {
  const { t } = useScopedTranslate(locale);
  const hasMenu = ['text', 'paragraph'].includes(fieldType);
  return (
    <ComboContainer data-has-menu={hasMenu}>
      <Button
        key={field.field}
        variant="primary-blue"
        size="sm"
        text={t('core.add')}
        iconLeft={<IconReturn />}
        onClick={() => onSelect?.(field, 'value')}
        style={{ flexShrink: 0 }}
      />
      {hasMenu && (
        <Menu
          width={pxToRem(312)}
          buttonProps={{
            variant: 'primary-blue',
            size: 'sm',
            iconLeft: <IconDropdown />,
          }}
        >
          <Menu.Item
            label={t('addFieldLabelAndMapping')}
            extraIcon={
              <Stack orientation="horizontal" space={1}>
                <Button variant="secondary" size="xs">
                  <IconFormatText size={16} />
                </Button>
                <Button variant="primary-blue" size="xs">
                  <IconBranch size={16} />
                </Button>
              </Stack>
            }
            onSelect={() => onSelect?.(field, 'labelAndValue')}
          />
          <Menu.Item
            label={t('addFieldLabel')}
            extraIcon={
              <Button variant="secondary" size="xs">
                <IconFormatText size={16} />
              </Button>
            }
            onSelect={() => onSelect(field, 'label')}
          />
        </Menu>
      )}
    </ComboContainer>
  );
};

type GroupTitleProps = Pick<VariableGroupProps, 'isCollapsed' | 'isActionGroup'> & {
  group: Pick<FieldMappingGroup, 'label'>;
  count?: number;
  description?: string;
  icon?: React.ReactNode;
  onClick(): void;
};

const GroupTitle = ({
  group,
  isCollapsed,
  isActionGroup,
  count,
  description,
  onClick,
  icon,
}: GroupTitleProps) => {
  return (
    <Stack
      orientation="horizontal"
      space={2}
      align="center"
      justify={isActionGroup ? 'space-between' : 'start'}
      onClick={onClick}
      style={{ cursor: 'pointer' }}
    >
      <Stack orientation="horizontal" space={2} align="center">
        {icon}
        <div>
          <Text text={group.label} font="semibold14" />
          {description && <Text text={description} font="regular12" color="textSecondary" />}
        </div>
      </Stack>
      {count !== undefined && <Badge>{count}</Badge>}
      <IconDropdown color="buttonIconPositive" rotateDegrees={isCollapsed ? 270 : 0} />
    </Stack>
  );
};

type FieldLabelCardProps = MakePropertiesRequired<
  Pick<
    FieldMappingSidebarProps,
    'label' | 'fieldType' | 'editorRef' | 'onManualSelect' | 'manualData'
  > & {
    isShowingAll: boolean;
    hasBackground?: boolean;
  },
  'label' | 'fieldType'
>;

const FieldLabelCard = ({
  label,
  fieldType,
  isShowingAll,
  editorRef,
  onManualSelect,
  manualData,
  hasBackground = false,
}: FieldLabelCardProps) => {
  const { t } = useScopedTranslate(locale);
  const Icon = fieldTypeIconMap[fieldType ?? 'text'] ?? IconTextShort;
  return (
    <FieldLabelCardContainer $hasBackground={hasBackground}>
      <Stack space={2}>
        <Stack orientation="horizontal" space={2} align="top" justify="space-between">
          <div>
            <Text text={unasteriskLabel(label)} font="semibold14" />
            <Text
              text={fieldTypeLabelMap[fieldType] ?? ''}
              font="regular12"
              color="textSecondary"
            />
          </div>
          <Icon color="elementPurple80" />
        </Stack>
        {isShowingAll && <Text text={t('displayingAll')} font="regular12" color="textSecondary" />}
        <ManuallyInputButton
          editorRef={editorRef}
          fieldType={fieldType}
          onManualSelect={onManualSelect}
          manualData={manualData}
        />
      </Stack>
    </FieldLabelCardContainer>
  );
};

export type ManuallyInputButtonProps = Pick<
  FieldMappingSidebarProps,
  'editorRef' | 'fieldType' | 'onManualSelect' | 'manualData'
>;

const ManuallyInputButton = ({
  editorRef,
  fieldType,
  onManualSelect,
  manualData,
}: ManuallyInputButtonProps) => {
  if (fieldType === 'phone') {
    return <PhoneManualField editorRef={editorRef} />;
  }
  if (fieldType === 'select') {
    return <SelectManualField editorRef={editorRef} options={manualData} />;
  }
  if (fieldType === 'date') {
    return <DateManualField editorRef={editorRef} />;
  }
  if (fieldType === 'number') {
    return <NumberManualField editorRef={editorRef} />;
  }
  if (fieldType === 'decimal') {
    return <NumberManualField editorRef={editorRef} isDecimal />;
  }
  if (fieldType === 'file' && onManualSelect) {
    return (
      <Stack orientation="horizontal">
        <FileManualField editorRef={editorRef} onManualSelect={onManualSelect} />
      </Stack>
    );
  }
  return null;
};

const FieldLabelCardContainer = styled.div<Transient<FieldLabelCardProps, 'hasBackground'>>(
  ({ $hasBackground }) => ({
    padding: pxToRem(16),
    borderRadius: pxToRem(8),
    border: `${pxToRem(1)} solid ${theme.color.border10}`,
    backgroundColor: $hasBackground ? theme.color.backgroundPrimary : 'transparent',
  })
);

const FieldsContainer = styled.div({
  ...scrollbarStyle,
  flexGrow: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: `0 ${pxToRem(24)}`,
  margin: `0 ${pxToRem(-24)}`,
});

const Container = styled.div({
  height: '100%',
  '.search-box-container': {
    flexGrow: 1,
  },
});

const BORDER_WIDTH = 1;
export const FieldIndentBorder = styled.div<{ $isValue?: boolean }>(({ $isValue }) => ({
  width: pxToRem(BORDER_WIDTH),
  backgroundColor: theme.color.uiTrans20,
  marginLeft: pxToRem(12),
  marginRight: pxToRem(12 - BORDER_WIDTH),
  ...($isValue ? { marginTop: pxToRem(-20) } : {}),
}));

export const locale = {
  en: {
    fieldMap: 'Field map',
    collapseAll: 'Collapse all',
    expandAll: 'Expand all',
    traveller: 'Traveller',
    travellerDescription: 'Global profile fields',
    company: 'Company',
    displayingAll: 'Displaying all available mappable fields',
    actionsInJourney: 'Actions in journey',
    addPhoneNumber: 'Add telephone number',
    dateLabel: 'Date picker',
    manuallySelectDate: 'Manually select date',
    uploadFile: 'Upload file',
    manuallySelectValue: 'Manually select value',
    noResultsFound: 'No results found',
    noResultsMatchingSearch: 'No results matching your search',
    noMappableFields: 'No mappable fields',
    possibleReasons: 'Possible reasons',
    noActionsAbove: 'There are no actions above selected action',
    noFieldsSameType: 'There are no fields of the same type as the selected field',
    optionValue: 'Option value',
    optionLabel: 'Option label',
    numberLabel: 'Number',
    addNumber: 'Add number',
    addFieldLabelAndMapping: 'Add field label & mapping',
    addFieldLabel: 'Add field label',
  },
};
